import { ArrowLeft, Check, RefreshCw, Sparkles } from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SchemaAgentKickoff } from '@/components/schema/agent/SchemaAgentKickoff'
import { SchemaAgentProgress } from '@/components/schema/agent/SchemaAgentProgress'
import { SchemaSources } from '@/components/schema/agent/SchemaSources'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type { SchemaSession, SchemaSessionStartBody } from '@/services/api/schemaSessions'
import { DraftSchemaEditor } from './DraftSchemaEditor'
import {
  isSessionActive,
  isSessionLocked,
  useSchemaSessionDocuments,
} from './hooks/use-schema-session'
import { useSchemaRunProgress, useSchemaSessionEvents } from './hooks/use-schema-session-events'
import { useWorkbench } from './hooks/use-workbench'
import { ProposalDecisionBar } from './ProposalDecisionBar'
import { ProposalDiffCard } from './ProposalDiffCard'
import { ProposalReviewNavigation } from './ProposalReviewNavigation'
import { ProposalSchemaPreview } from './ProposalSchemaPreview'
import { SchemaConversationContext, type SchemaQuestion } from './SchemaConversationContext'
import { WorkbenchConversation } from './WorkbenchConversation'

interface WorkbenchNavigation {
  back: () => void
  newSession: () => void
  accepted: (id: string) => Promise<void>
}

function intakeBody(form: SchemaSession['intake']): SchemaSessionStartBody {
  return {
    documentIds: form.document_ids,
    caseType: form.case_type,
    investigationGoal: form.investigation_goal ?? '',
    primaryQuestions: form.primary_questions,
    focusDomains: form.focus_domains ?? [],
    customQuestions: form.custom_questions,
    subjects: form.subjects,
    evidenceTypes: form.evidence_types,
    timeWindow: form.time_window,
    geographicScope: form.geographic_scope,
    notes: form.notes,
  }
}

type WorkbenchActions = ReturnType<typeof useWorkbench>

export function SchemaSessionWorkbench({
  session,
  navigation,
}: {
  session: SchemaSession
  navigation: WorkbenchNavigation
}) {
  const [intakeOpen, setIntakeOpen] = useState(false)
  const [question, setQuestion] = useState<SchemaQuestion | null>(null)
  const actions = useWorkbench(session, navigation.accepted)
  const panes = useWorkbenchPanes(session, actions, question)
  return (
    <SchemaConversationContext.Provider value={setQuestion}>
      <WorkbenchLayout
        session={session}
        header={
          <WorkbenchActionsBar
            session={session}
            actions={actions}
            navigation={{ ...navigation, intake: () => setIntakeOpen(true) }}
          />
        }
        panes={panes}
      />
      <WorkbenchDialogs
        session={session}
        actions={actions}
        intake={{ open: intakeOpen, setOpen: setIntakeOpen }}
      />
    </SchemaConversationContext.Provider>
  )
}

function useWorkbenchPanes(
  session: SchemaSession,
  actions: WorkbenchActions,
  question: SchemaQuestion | null
) {
  const events = useSchemaSessionEvents({
    caseId: session.caseId,
    sessionId: session.id,
    runId: session.latestRun?.id,
    active: isSessionActive(session),
    agentThreadId: session.agentThreadId,
  })
  const progress = useSchemaRunProgress(session.latestRun, events.events)
  const proposal = session.pendingProposal
  return {
    review: proposal ? <WorkbenchProposal session={session} actions={actions} /> : undefined,
    decision: proposal ? (
      <ProposalDecisionBar
        hasErrors={proposal.errors.length > 0}
        actions={{
          busy: actions.busy,
          apply: () => void actions.resolve(true),
          discard: () => void actions.resolve(false),
        }}
      />
    ) : undefined,
    draft: (
      <WorkbenchDraft
        session={session}
        actions={actions}
        run={{ progress, connected: events.isConnected }}
      />
    ),
    chat: (
      <WorkbenchConversation
        session={session}
        actions={actions}
        question={question}
        lastTool={progress.lastTool}
      />
    ),
  }
}

function WorkbenchProposal({
  session,
  actions,
}: {
  session: SchemaSession
  actions: WorkbenchActions
}) {
  if (!session.pendingProposal) return null
  return (
    <ProposalDiffCard
      key={session.latestRun?.id ?? 'pending'}
      base={(session.pendingBase ?? {}) as unknown as Record<string, unknown>}
      proposal={session.pendingProposal}
      reference={
        session.workingSchema || session.draft ? (
          <DraftSchemaEditor session={session} onChange={actions.update} readOnly />
        ) : undefined
      }
    />
  )
}

function WorkbenchDraft({
  session,
  actions,
  run,
}: {
  session: SchemaSession
  actions: WorkbenchActions
  run: { progress: ReturnType<typeof useSchemaRunProgress>; connected: boolean }
}) {
  if (session.workingSchema || session.draft)
    return (
      <DraftSchemaEditor
        session={session}
        onChange={actions.update}
        readOnly={isSessionLocked(session) || actions.busy || !!session.pendingProposal}
      />
    )
  if (session.latestRun?.status === 'awaiting_input') return <AwaitingAnswersNote />
  // A repair run keeps the invalid proposal it retries; show the run first.
  if (isSessionActive(session))
    return (
      <SchemaAgentProgress
        isConnected={run.connected}
        startedAt={session.latestRun?.startedAt ?? null}
        files={run.progress.files}
        stepsCompleted={run.progress.steps}
        lastTool={run.progress.lastTool}
      />
    )
  const preview = session.pendingProposal ?? session.latestRun?.proposal
  return preview ? <ProposalSchemaPreview proposal={preview} /> : null
}

function WorkbenchActionsBar({
  session,
  actions,
  navigation,
}: {
  session: SchemaSession
  actions: WorkbenchActions
  navigation: WorkbenchNavigation & { intake: () => void }
}) {
  const { t } = useTranslation()
  const active = isSessionLocked(session)
  const disabled = active || !!session.pendingProposal || actions.busy
  return (
    <>
      <Button size="sm" variant="ghost" onClick={navigation.back}>
        <ArrowLeft className="size-4" />
        {t('schemaAgent.workbench.back')}
      </Button>
      <Button size="sm" variant="outline" disabled={disabled} onClick={navigation.intake}>
        <RefreshCw className="size-4" />
        {t('schemaAgent.workbench.intake')}
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={active || actions.busy}
        onClick={navigation.newSession}
      >
        {t('schemaAgent.workbench.newSession')}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={actions.busy}
        onClick={() => void actions.discard()}
      >
        {t('schemaAgent.review.discard')}
      </Button>
      <AcceptSchemaAction session={session} actions={actions} disabled={disabled} />
    </>
  )
}

function AcceptSchemaAction({
  session,
  actions,
  disabled,
}: {
  session: SchemaSession
  actions: WorkbenchActions
  disabled: boolean
}) {
  const { t } = useTranslation()
  if (session.pendingProposal) return null
  return (
    <Button
      size="sm"
      variant="success"
      disabled={disabled || !session.draft}
      onClick={() => void actions.accept()}
    >
      <Check className="size-4" />
      {t('schemaAgent.workbench.accept')}
    </Button>
  )
}

function WorkbenchDialogs({
  session,
  actions,
  intake,
}: {
  session: SchemaSession
  actions: WorkbenchActions
  intake: { open: boolean; setOpen: (open: boolean) => void }
}) {
  const { t } = useTranslation()
  const form = useMemo(() => intakeBody(session.intake), [session.intake])
  return (
    <>
      <Sheet open={intake.open} onOpenChange={intake.setOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t('schemaAgent.workbench.intake')}</SheetTitle>
            <SheetDescription>{t('schemaAgent.workbench.regenerateHint')}</SheetDescription>
          </SheetHeader>
          <RegenerateIntake
            session={session}
            form={form}
            busy={actions.busy}
            onSubmit={body =>
              void actions.regenerate(body).then(success => success && intake.setOpen(false))
            }
          />
        </SheetContent>
      </Sheet>
      <HeadMovedDialog actions={actions} />
    </>
  )
}

function HeadMovedDialog({ actions }: { actions: WorkbenchActions }) {
  const { t } = useTranslation()
  return (
    <Dialog
      open={actions.headWarning !== undefined}
      onOpenChange={open => !open && actions.setHeadWarning(undefined)}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('schemaAgent.workbench.headMoved')}</DialogTitle>
          <DialogDescription>{t('schemaAgent.workbench.headMovedHint')}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => actions.setHeadWarning(undefined)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="success"
            disabled={actions.busy}
            onClick={() => void actions.accept(actions.headWarning)}
          >
            <Check className="size-4" />
            {t('schemaAgent.workbench.confirmAccept')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function WorkbenchLayout({
  session,
  panes,
  header,
}: {
  session: SchemaSession
  panes: { draft: ReactNode; chat: ReactNode; review?: ReactNode; decision?: ReactNode }
  header: ReactNode
}) {
  const { t } = useTranslation()
  return (
    <ProposalReviewNavigation>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-6 xl:overflow-hidden">
          <WorkbenchHeading session={session} actions={header} />
          <SchemaSources
            initial={session.initializationSources ?? []}
            minimum={session.compiledIntake.minimum_schema}
            proposal={
              session.pendingProposal ?? session.reviewProposal ?? session.latestRun?.proposal
            }
          />
          <div className="grid min-h-[650px] shrink-0 grid-cols-1 overflow-hidden rounded-xl border bg-card xl:min-h-0 xl:flex-1 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
            {panes.review ? (
              <div className="h-[min(42rem,80dvh)] min-h-80 min-w-0 overflow-hidden xl:h-auto xl:min-h-0 xl:border-r">
                {panes.review}
              </div>
            ) : (
              <WorkingSchemaPane session={session}>{panes.draft}</WorkingSchemaPane>
            )}
            <section className="flex min-h-[600px] min-w-0 flex-col overflow-hidden border-t xl:min-h-0 xl:border-t-0">
              <h2 className="shrink-0 border-b px-4 py-3 text-sm font-semibold">
                {t('schemaAgent.workbench.conversation')}
              </h2>
              {panes.chat}
            </section>
          </div>
        </div>
        {panes.decision}
      </div>
    </ProposalReviewNavigation>
  )
}

function WorkbenchHeading({ session, actions }: { session: SchemaSession; actions: ReactNode }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="size-4 text-primary" />
          {t('schemaAgent.title')}
        </p>
        <h1 className="mt-1 text-xl font-semibold">
          {session.workingSchema?.name ??
            session.draft?.name ??
            session.pendingProposal?.name ??
            session.latestRun?.proposal?.name ??
            t('schemaAgent.workbench.composingTitle')}
        </h1>
      </div>
      <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
  )
}

function WorkingSchemaPane({ session, children }: { session: SchemaSession; children: ReactNode }) {
  const { t } = useTranslation()
  const status = session.draft
    ? 'unversioned'
    : session.workingSchema
      ? 'accepted'
      : isSessionLocked(session)
        ? 'inProgress'
        : 'notApplied'
  return (
    <section className="min-w-0 overflow-y-auto p-4 xl:border-r">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">
          {t(
            session.workingSchema || session.draft
              ? 'schemaAgent.workbench.draft'
              : 'schemaAgent.workbench.proposalPreview'
          )}
        </h2>
        <Badge variant="secondary">{t(`schemaAgent.workbench.${status}`)}</Badge>
      </div>
      {children}
    </section>
  )
}

/** The left pane while the run waits on the analyst: the questions are in the conversation. */
export function AwaitingAnswersNote() {
  const { t } = useTranslation()
  return (
    <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
      {t('schemaAgent.workbench.awaitingAnswers')}
    </p>
  )
}

function RegenerateIntake({
  session,
  form,
  busy,
  onSubmit,
}: {
  session: SchemaSession
  form: SchemaSessionStartBody
  busy: boolean
  onSubmit: (body: SchemaSessionStartBody) => void
}) {
  const { t } = useTranslation()
  const documents = useSchemaSessionDocuments(session.caseId, true)
  if (documents.isError)
    return (
      <div role="alert">
        <p>{t('schemaAgent.error.loadDocuments')}</p>
        <Button onClick={() => void documents.refetch()}>{t('schemaAgent.error.retry')}</Button>
      </div>
    )
  if (documents.isPending) return <p role="status">{t('schemaAgent.kickoff.loading')}</p>
  return (
    <SchemaAgentKickoff
      documents={documents.data}
      context={{
        caseId: session.caseId,
        initial: form,
        submitLabel: t('schemaAgent.workbench.regenerate'),
      }}
      isPending={busy}
      onStart={onSubmit}
    />
  )
}
